import { LowerCasePipe } from '@angular/common';
import { Component, computed, effect, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TextTrackingComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/text-tracking/text-tracking.component';
import { AnalyticsDetailsTableComponent } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { EDeliverableType } from '@enums/campaign.enum';
import { ITextTrackingAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { TStatusBadge, TStatusBadgeColorScheme } from '@shared/components/status-badge/status-badge.interface';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { FindingVisualizerService } from '@utilities/finding-visualizer.service';
import { TextPostAnalyticsBaseDirective } from '../text-post-analytics-base/text-post-analytics-base.directive';
import { ITextTrackingPostAnalyticsTableData } from './text-tracking-post-analytics.interface';

@Component({
  imports: [
    TranslateModule,
    AnalyticsDetailsTableComponent,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    MatTooltipModule,
    TextTrackingComponent,
    LowerCasePipe,
    MatMenuModule,
    DotsSpinnerComponent,
    IconComponent,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-text-tracking-post-analytics',
  templateUrl: './text-tracking-post-analytics.component.html',
})
export class TextTrackingPostAnalyticsComponent extends TextPostAnalyticsBaseDirective<
  ITextTrackingPostAnalyticsTableData,
  ITextTrackingAnalytics
> {
  public override readonly deliverableType = EDeliverableType.textTracking;
  public override readonly kpiCardData = null;
  public override readonly displayedColumns: string[] = ['source', 'text', 'actions'];
  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.textTrackings?.find((t) => t.deliverableId === deliverableId);
    }

    return null;
  });
  public override readonly contentData = null;

  constructor() {
    super();
  }

  protected override setKeywordCounts(): void {
    const keywords = this.currentDeliverable()?.keywords ?? [];
    const keywordNames = keywords.map((k) => k.keyword);

    const findings = keywords.flatMap((k) => (k.findings ?? []).map((finding) => finding.text));
    this.keywordCounts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(keywordNames, findings);

    AnalyticsDetailsUtilitiesService.mapKeywordCountsToDeliverableForm(this.deliverableForm(), this.keywordCounts);
  }

  private readonly findingVisualizerService = inject(FindingVisualizerService);

  protected override prepareTableData(): void {
    const keywords = this.currentDeliverable()?.keywords ?? [];
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    const visualizers = this.findingVisualizerService.buildFromKeywords(keywords, removedIds);

    const tableData: ITextTrackingPostAnalyticsTableData[] = visualizers.map((visualizer) => ({
      ...visualizer,
      source: visualizer.findings[0].source,
      text: visualizer.findings[0].text,
    }));

    this.dataSource.data = tableData;
    this.dataCount.set(tableData.length);
  }

  protected override setKeywordCardList(): void {
    const textTracking = this.currentDeliverable();

    if (textTracking?.keywords && textTracking.keywords.length) {
      const kpiCardList = textTracking.keywords.map((k) => {
        const badgeStatus: TStatusBadge = this.keywordCounts[k.keyword] ? 'found' : 'notFound';
        const badgeColorScheme: TStatusBadgeColorScheme = 'deliverable';

        return {
          badgeColorScheme,
          badgeStatus,
          icon: 'keyword.svg',
          id: `${k.keyword}-kpi-card`,
          keyword: k.keyword,
        };
      });

      this.keywordCardList.set(kpiCardList);
    }
  }

  protected override setKeywordsEffect(): void {
    effect(() => {
      this.keywords = this.currentDeliverable()?.keywords?.map((k) => k.keyword) ?? [];
    });
  }
}
